import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Switch,
  IconButton,
  Snackbar,
  Alert,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { supabase } from '../../supabaseClient';

function RolesAdminPage() {
  const [roles, setRoles] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRole, setCurrentRole] = useState({
    name: '',
    can_view_all_users: false,
    can_view_all_sectors: false,
    can_access_dashboard: false,
    can_access_time_entry: false,
    can_access_mandate_management: false,
    can_access_releve_code_management: false,
    can_access_sector_management: false,
    can_access_activities_admin: false,
    can_access_pointage_mensuel: false,
    can_access_rapport_mensuel: false,
    can_access_monthly_activities: false,
    can_access_user_admin: false,
    can_access_roles_admin: false,
    can_access_sector_calendar: false,
    can_manage_sector_calendar: false
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchRoles = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('*')
        .order('name', { ascending: true });  // Ajout du tri
      if (error) throw error;
      setRoles(data);
    } catch (error) {
      console.error('Erreur lors du chargement des rôles:', error);
      showSnackbar('Erreur lors du chargement des rôles', 'error');
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleOpenDialog = (role = null) => {
    if (role) {
      setCurrentRole(role);
      setIsEditing(true);
    } else {
      setCurrentRole({
        name: '',
        can_view_all_users: false,
        can_view_all_sectors: false,
        can_access_dashboard: false,
        can_access_time_entry: false,
        can_access_mandate_management: false,
        can_access_releve_code_management: false,
        can_access_sector_management: false,
        can_access_activities_admin: false,
        can_access_pointage_mensuel: false,
        can_access_rapport_mensuel: false,
        can_access_monthly_activities: false,
        can_access_user_admin: false,
        can_access_roles_admin: false,
        can_access_sector_calendar: false,
        can_manage_sector_calendar: false
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRole({
      name: '',
      can_view_all_users: false,
      can_view_all_sectors: false,
      can_access_dashboard: false,
      can_access_time_entry: false,
      can_access_mandate_management: false,
      can_access_releve_code_management: false,
      can_access_sector_management: false,
      can_access_activities_admin: false,
      can_access_pointage_mensuel: false,
      can_access_rapport_mensuel: false,
      can_access_monthly_activities: false,
      can_access_user_admin: false,
      can_access_roles_admin: false,
      can_access_sector_calendar: false,
      can_manage_sector_calendar: false
    });
    setIsEditing(false);
  };

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setCurrentRole((prev) => ({
      ...prev,
      [name]: name.startsWith('can_') ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    try {
      let result;
      if (isEditing) {
        result = await supabase.from('roles').update(currentRole).eq('id', currentRole.id);
      } else {
        result = await supabase.from('roles').insert(currentRole);
      }
      if (result.error) throw result.error;
      handleCloseDialog();
      fetchRoles();
      showSnackbar(isEditing ? 'Rôle mis à jour avec succès' : 'Rôle créé avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (roleId) => {
    try {
      const { error } = await supabase.from('roles').delete().eq('id', roleId);
      if (error) throw error;
      fetchRoles();
      showSnackbar('Rôle supprimé avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la suppression du rôle:', error);
      showSnackbar('Erreur lors de la suppression du rôle', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Ajout du mapping de traduction
  const permissionLabels = {
    'can_access_dashboard': 'Tableau de bord',
    'can_access_monthly_activities': 'Activités mensuelles',
    'can_access_time_entry': 'Saisie des pointages',
    'can_access_pointage_mensuel': 'Pointage mensuel',
    'can_access_rapport_mensuel': 'Rapport mensuel',
    'can_access_mandate_management': 'Gestion des moyens',
    'can_access_releve_code_management': 'Gestion des codes de relève',
    'can_access_sector_management': 'Gestion des secteurs',
    'can_access_activities_admin': 'Gestion des activités',
    'can_access_user_admin': 'Gestion des utilisateurs',
    'can_access_roles_admin': 'Gestion des rôles',
    'can_view_all_users': 'Voir tous les utilisateurs du secteur',
    'can_view_all_sectors': 'Voir tous les secteurs',
    'can_access_sector_calendar': 'Accès au calendrier secteur',
    'can_manage_sector_calendar': 'Gestion du calendrier secteur'
  };

  // Fonction utilitaire pour trier les permissions selon l'ordre de permissionLabels
  const sortPermissionsByOrder = (entries) => {
    const permissionOrder = Object.keys(permissionLabels);
    return entries.sort((a, b) => {
      const indexA = permissionOrder.indexOf(a[0]);
      const indexB = permissionOrder.indexOf(b[0]);
      return indexA - indexB;
    });
  };

  return (
    <Box sx={{ width: '100%', padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
        <Typography variant="h4">Gestion des Rôles</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Nouveau Rôle
        </Button>
      </Box>

      <Grid container spacing={2}>
        {roles.map((role) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={role.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" component="div">
                    {role.name}
                  </Typography>
                  <Box>
                    <IconButton color="primary" onClick={() => handleOpenDialog(role)} size="small">
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(role.id)} size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Grid container spacing={1}>
                  {sortPermissionsByOrder(
                    Object.entries(role)
                      .filter(([key]) => key.startsWith('can_'))
                  ).map(([key, value]) => (
                    <Grid item xs={12} key={key}>
                      <FormControlLabel
                        control={<Switch checked={value} disabled size="small" />}
                        label={permissionLabels[key] || key}
                        sx={{ 
                          m: 0,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                            color: value ? 'text.primary' : 'text.secondary'
                          }
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Modifier le Rôle' : 'Nouveau Rôle'}</DialogTitle>
        <DialogContent>
          <TextField
            name="name"
            label="Nom"
            value={currentRole.name}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sortPermissionsByOrder(
              Object.entries(currentRole)
                .filter(([key]) => key.startsWith('can_'))
            ).map(([key, value]) => (
              <FormControlLabel
                key={key}
                control={<Switch checked={value} onChange={handleChange} name={key} />}
                label={permissionLabels[key]}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default RolesAdminPage;
